import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { cancelSession, useSessionMessages, useSessionStatus, useSupersedMessages } from "../api/sessions";
import { useChatStream } from "../hooks/useChatStream";
import { useStreams } from "../streams/StreamsProvider";
import { useTraceTurns } from "../hooks/useTraceTurns";
import type { Message, TraceTurn } from "../types";
import { queryKeys } from "../types";

interface ChatSessionContextValue {
  sessionId: string | undefined;
  routeBase: string;
  messages: Message[];
  traceTurns: TraceTurn[];
  isStreaming: boolean;
  isLoading: boolean;
  isMessagesError: boolean;
  refetchMessages: () => void;
  isBackgroundRunning: boolean;
  aborted: boolean;
  hasMoreMessages: boolean;
  isLoadingOlder: boolean;
  sendMessage: (message: string, opts?: { sessionTitle?: string }) => void;
  stopStreaming: () => void;
  editMessage: (messageId: string, newContent: string) => Promise<void>;
  loadOlderMessages: () => void;
}

const ChatSessionContext = createContext<ChatSessionContextValue | null>(null);

export const useChatSession = (): ChatSessionContextValue => {
  const ctx = useContext(ChatSessionContext);
  if (!ctx)
    throw new Error("useChatSession must be used within ChatSessionProvider");
  return ctx;
};

interface Props {
  sessionId: string | undefined;
  children: ReactNode;
  /** Agent config the backend runs for this session (defaults to Paperclip chat). */
  agentConfigPath?: string;
  /** Route prefix used when a new session is created (e.g. "/chat", "/peer-review"). */
  routeBase?: string;
  /** Receives a new session's id instead of navigating to `routeBase`. */
  onSessionCreated?: (sessionId: string) => void;
  /** Data curation a new session belongs to. */
  extractionId?: string;
  /** This is the Data Curation page chat. */
  dataCurationChat?: boolean;
  /** The fast dataset beside this chat; the agent gets it with every message. */
  fastDatasetId?: string;
  /** Or the share token of a shared fast dataset, which the agent gets read-only. */
  fastDatasetShare?: string;
  /** Also runs when the user stops the agent, such as to end later turns of a collection. */
  onStop?: () => void;
}

export const ChatSessionProvider = ({
  sessionId,
  children,
  agentConfigPath,
  routeBase = "/chat",
  onSessionCreated,
  extractionId,
  dataCurationChat,
  fastDatasetId,
  fastDatasetShare,
  onStop,
}: Props) => {
  const queryClient = useQueryClient();
  const { abortStreamsForSession } = useStreams();
  const {
    messages,
    hasMore,
    isLoading,
    isError: isMessagesError,
    isLoadingOlder,
    loadOlderMessages,
    resetOlderMessages,
    refetch: refetchMessages,
  } = useSessionMessages(sessionId);
  const { currentStreamingState, sendMessage: rawSendMessage } =
    useChatStream(sessionId, {
      agentConfigPath,
      routeBase,
      onSessionCreated,
      extractionId,
      dataCurationChat,
      fastDatasetId,
      fastDatasetShare,
    });

  const isStreaming = currentStreamingState?.isStreaming ?? false;
  const aborted = currentStreamingState?.aborted ?? false;

  // Background-running detection
  const { data: statusData, isLoading: isStatusLoading } = useSessionStatus(
    sessionId,
    !!sessionId && !isStreaming,
  );
  const isBackgroundRunning = statusData?.status === "running";

  // Refetch messages only when a background run finishes (running → idle).
  // Until they arrive, the run counts as active, so that a call whose result
  // is not loaded yet does not show as stopped.
  const [refreshingAfterRun, setRefreshingAfterRun] = useState(false);
  const prevBackgroundRunning = useRef(isBackgroundRunning);
  useLayoutEffect(() => {
    if (
      prevBackgroundRunning.current &&
      !isBackgroundRunning &&
      sessionId &&
      !isStreaming
    ) {
      setRefreshingAfterRun(true);
      void queryClient
        .invalidateQueries({ queryKey: queryKeys.messages(sessionId) })
        .finally(() => setRefreshingAfterRun(false));
    }
    prevBackgroundRunning.current = isBackgroundRunning;
  }, [isBackgroundRunning, sessionId, isStreaming, queryClient]);
  const runActive =
    isStreaming || isBackgroundRunning || isStatusLoading || refreshingAfterRun;

  const traceTurns = useTraceTurns(messages, currentStreamingState, runActive);

  const sendMessage = useCallback(
    (message: string, opts?: { sessionTitle?: string }) => {
      rawSendMessage(message, opts);
    },
    [rawSendMessage],
  );

  const stopStreaming = useCallback(() => {
    abortStreamsForSession(sessionId);
    const cancelId = sessionId ?? currentStreamingState?.sessionId;
    if (cancelId) {
      cancelSession(cancelId).catch(() => { });
    }
    onStop?.();
  }, [abortStreamsForSession, sessionId, currentStreamingState?.sessionId, onStop]);

  const supersedMessages = useSupersedMessages(sessionId);

  const editMessage = useCallback(
    async (messageId: string, newContent: string) => {
      if (!sessionId) {
        toast.error("Cannot edit — no active session.");
        return;
      }
      if (messageId.startsWith("pending-")) {
        toast.error("Please wait for the message to finish sending.");
        return;
      }

      try {
        abortStreamsForSession(sessionId);
        cancelSession(sessionId).catch(() => { });
        await supersedMessages(messageId);
        resetOlderMessages();
        rawSendMessage(newContent);
      } catch (error) {
        console.error("Failed to edit message:", error);
        toast.error("Failed to edit message. Please try again.");
      }
    },
    [
      sessionId,
      abortStreamsForSession,
      supersedMessages,
      resetOlderMessages,
      rawSendMessage,
    ],
  );

  return (
    <ChatSessionContext.Provider
      value={{
        sessionId,
        routeBase,
        messages,
        traceTurns,
        isStreaming,
        isLoading,
        isMessagesError,
        refetchMessages,
        isBackgroundRunning,
        aborted,
        hasMoreMessages: hasMore,
        isLoadingOlder,
        sendMessage,
        stopStreaming,
        editMessage,
        loadOlderMessages,
      }}
    >
      {children}
    </ChatSessionContext.Provider>
  );
};
